import React from 'react';
import { motion } from 'framer-motion';

/**
 * Reusable Button component with coffee-themed styling
 * @param {Object} props - Component props
 * @param {string} props.variant - Button variant: 'primary', 'secondary', 'outline'
 * @param {string} props.size - Button size: 'sm', 'md', 'lg'
 * @param {React.ReactNode} props.children - Button content
 * @param {Function} props.onClick - Click handler
 * @param {string} props.href - External link URL
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.className - Additional CSS classes
 */
const Button = ({
  variant = 'primary',
  size = 'md',
  children,
  onClick,
  href,
  disabled = false,
  className = '',
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-bold rounded-2xl transition-all duration-500 transform focus:outline-none focus:ring-4 focus:ring-offset-2 backdrop-blur-sm relative overflow-hidden group';
  
  const variantClasses = {
    primary: 'bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 hover:from-amber-600 hover:via-orange-600 hover:to-red-600 text-white shadow-2xl hover:shadow-3xl focus:ring-amber-300 before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/20 before:to-transparent before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-500',
    secondary: 'bg-white/80 backdrop-blur-lg hover:bg-white/90 text-coffee-700 border border-white/30 shadow-xl hover:shadow-2xl focus:ring-coffee-300 hover:border-white/50',
    outline: 'bg-white/10 backdrop-blur-md border-2 border-amber-400/50 text-amber-700 hover:bg-amber-500 hover:text-white hover:border-amber-500 focus:ring-amber-300 shadow-lg hover:shadow-xl'
  };
  
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };
  
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';
  
  const buttonClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${disabledClasses} ${className}`;
  
  const buttonContent = (
    <motion.button
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      whileHover={!disabled ? { 
        scale: 1.05, 
        y: -2,
        transition: { duration: 0.3, ease: "easeOut" }
      } : {}}
      whileTap={!disabled ? { 
        scale: 0.98, 
        y: 0,
        transition: { duration: 0.1 }
      } : {}}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      {...props}
    >
      <span className="relative z-10">{children}</span>
    </motion.button>
  );
  
  if (href && !disabled) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="inline-block">
        {buttonContent}
      </a>
    );
  }
  
  return buttonContent;
};

export default Button;